class CartDiscount extends HTMLElement {
  static SESSION_KEY = 'cartManualDiscountCodes';
  static clearStarted = false;

  constructor() {
    super();
    this.onApplyClick = this.onApply.bind(this);
    this.onKeydown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.onApply();
      }
    };
  }

  connectedCallback() {
    this.input = this.querySelector('.cart__discount-input') || this.querySelector('#Cart-discount');
    this.applyButton = this.querySelector('.cart__discount-apply');
    this.errorElement = this.querySelector('.cart__discount-error');
    this.successElement = this.querySelector('.cart__discount-success');

    if (!this.input || !this.applyButton) return;

    this.applyButton.addEventListener('click', this.onApplyClick);
    this.input.addEventListener('keydown', this.onKeydown);

    CartDiscount.clearUnapprovedDiscounts();
  }

  disconnectedCallback() {
    if (this.applyButton) {
      this.applyButton.removeEventListener('click', this.onApplyClick);
    }
    if (this.input) {
      this.input.removeEventListener('keydown', this.onKeydown);
    }
  }

  isDrawerContext() {
    return !!this.closest('cart-drawer');
  }

  static getManualCodes() {
    try {
      const raw = sessionStorage.getItem(CartDiscount.SESSION_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed)
        ? parsed.map((code) => String(code).trim()).filter(Boolean)
        : [];
    } catch (error) {
      return [];
    }
  }

  static setManualCodes(codes) {
    try {
      const unique = [...new Set((codes || []).map((code) => String(code).trim()).filter(Boolean))];
      sessionStorage.setItem(CartDiscount.SESSION_KEY, JSON.stringify(unique));
    } catch (error) {
      // Ignore storage failures.
    }
  }

  static rememberManualCode(code) {
    const codes = CartDiscount.getManualCodes();
    if (!codes.some((existing) => existing.toUpperCase() === code.toUpperCase())) {
      codes.push(code);
    }
    CartDiscount.setManualCodes(codes);
  }

  static normalizeCode(code) {
    return String(code || '')
      .trim()
      .replace(/^#/, '')
      .toUpperCase();
  }

  getSectionsToRender() {
    const sections = [];
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (!this.isDrawerContext() && cartItems?.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: cartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    if (!this.isDrawerContext() && cartFooter?.dataset.id) {
      if (document.getElementById('cart-footer-offers')) {
        sections.push({
          id: 'cart-footer-offers',
          section: cartFooter.dataset.id,
          selector: '#cart-footer-offers',
        });
      }

      if (document.getElementById('cart-footer-subtotal')) {
        sections.push({
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '#cart-footer-subtotal',
        });
      }
    }

    if (document.getElementById('CartDrawer')) {
      sections.push({ id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' });
    }

    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });

    // Only update live region when it exists (cart page) — avoids injecting into announcement bar
    if (document.getElementById('cart-live-region-text')) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    return sections;
  }

  static getGlobalSectionsToRender() {
    const instance = document.querySelector('cart-discount');
    if (instance && typeof instance.getSectionsToRender === 'function') {
      return instance.getSectionsToRender();
    }

    const sections = [];
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');

    if (cartItems?.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: cartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    if (cartFooter?.dataset.id) {
      if (document.getElementById('cart-footer-offers')) {
        sections.push({
          id: 'cart-footer-offers',
          section: cartFooter.dataset.id,
          selector: '#cart-footer-offers',
        });
      }

      if (document.getElementById('cart-footer-subtotal')) {
        sections.push({
          id: 'main-cart-footer',
          section: cartFooter.dataset.id,
          selector: '#cart-footer-subtotal',
        });
      }
    }

    if (document.getElementById('CartDrawer')) {
      sections.push({ id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' });
    }

    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });

    if (document.getElementById('cart-live-region-text')) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      });
    }

    return sections;
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  static getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  static resolveSectionTarget(section) {
    // Never fall back to generic selectors like `.shopify-section` — that can
    // overwrite the announcement bar / header on product pages.
    if (section.id === 'cart-drawer' || section.section === 'cart-drawer') {
      return document.getElementById('CartDrawer') || document.querySelector('cart-drawer');
    }

    // Icon lives inside the header section — update only the <a>, never the whole header
    if (section.id === 'cart-icon-bubble' || section.section === 'cart-icon-bubble') {
      return document.getElementById('cart-icon-bubble');
    }

    if (section.id === 'cart-live-region-text' || section.section === 'cart-live-region-text') {
      return document.getElementById('cart-live-region-text');
    }

    if (section.selector && section.selector !== '.shopify-section') {
      const bySelector = document.querySelector(section.selector);
      if (bySelector) return bySelector;
    }

    const target = document.getElementById(section.id);
    if (!target) return null;

    if (section.selector && section.selector !== '.shopify-section') {
      return target.querySelector(section.selector) || target;
    }

    // Prefer an explicit section wrapper id only (do NOT closest() up into header)
    const sectionWrapper = document.getElementById(`shopify-section-${section.section || section.id}`);
    return sectionWrapper || target;
  }

  static sectionsFetchUrl(sectionIds) {
    const root = window.Shopify?.routes?.root || '/';
    const ids = Array.isArray(sectionIds) ? sectionIds.filter(Boolean).join(',') : sectionIds;
    return `${root}?sections=${ids}`;
  }

  clearMessages() {
    if (this.errorElement) {
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }
    if (this.successElement) {
      this.successElement.hidden = true;
      this.successElement.textContent = '';
    }
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
    if (this.successElement) this.successElement.hidden = true;
  }

  showSuccess(message) {
    if (!this.successElement) return;
    this.successElement.textContent = message;
    this.successElement.hidden = false;
    if (this.errorElement) this.errorElement.hidden = true;
  }

  isDiscountApplied(cart, code) {
    const normalizedCode = CartDiscount.normalizeCode(code);
    if (!normalizedCode) return false;

    const discountEntry = cart.discount_codes?.find(
      (discount) => CartDiscount.normalizeCode(discount.code) === normalizedCode
    );

    if (discountEntry) {
      return discountEntry.applicable !== false;
    }

    return (
      cart.cart_level_discount_applications?.some(
        (discount) => CartDiscount.normalizeCode(discount.title) === normalizedCode
      ) ||
      cart.items?.some((item) =>
        item.discounts?.some((discount) => CartDiscount.normalizeCode(discount.title) === normalizedCode)
      )
    );
  }

  async getExistingDiscountCodes() {
    const response = await fetch(`${routes.cart_url}.js`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    const cart = await response.json();
    return (cart.discount_codes || [])
      .filter((discount) => discount.applicable !== false)
      .map((discount) => discount.code);
  }

  setLoading(isLoading) {
    if (this.applyButton) {
      this.applyButton.disabled = isLoading;
      this.applyButton.classList.toggle('loading', isLoading);
    }
    if (this.input) this.input.disabled = isLoading;
    if (this?.classList) this.classList.toggle('cart__discount--loading', isLoading);
  }

  renderSections(cartData) {
    let rendered = false;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = cartData.sections?.[section.section];
      if (!sectionHtml) return;

      const sectionContent = CartDiscount.resolveSectionTarget(section);
      if (!sectionContent) return;

      const selector = section.selector || '.shopify-section';
      const innerHTML = this.getSectionInnerHTML(sectionHtml, selector);

      if (innerHTML) {
        sectionContent.innerHTML = innerHTML;
        rendered = true;
      }
    });

    return rendered;
  }

  static renderSectionsFromCart(cartData) {
    let rendered = false;

    CartDiscount.getGlobalSectionsToRender().forEach((section) => {
      const sectionHtml = cartData.sections?.[section.section];
      if (!sectionHtml) return;

      const sectionContent = CartDiscount.resolveSectionTarget(section);
      if (!sectionContent) return;

      const selector = section.selector || '.shopify-section';
      const innerHTML = CartDiscount.getSectionInnerHTML(sectionHtml, selector);
      if (innerHTML) {
        sectionContent.innerHTML = innerHTML;
        rendered = true;
      }
    });

    return rendered;
  }

  async refreshSectionsFallback() {
    const sections = this.getSectionsToRender();
    const sectionIds = [...new Set(sections.map((section) => section.section))];

    try {
      const response = await fetch(CartDiscount.sectionsFetchUrl(sectionIds));
      if (!response.ok) return;
      const data = await response.json();

      sections.forEach((section) => {
        const sectionHtml = data[section.section];
        const sectionContent = CartDiscount.resolveSectionTarget(section);
        if (!sectionHtml || !sectionContent) return;

        const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector || '.shopify-section');
        if (innerHTML) sectionContent.innerHTML = innerHTML;
      });
    } catch (error) {
      console.error(error);
    }
  }

  static async refreshSectionsFallback() {
    const sections = CartDiscount.getGlobalSectionsToRender();
    const sectionIds = [...new Set(sections.map((section) => section.section))];

    try {
      const response = await fetch(CartDiscount.sectionsFetchUrl(sectionIds));
      if (!response.ok) return;
      const data = await response.json();

      sections.forEach((section) => {
        const sectionHtml = data[section.section];
        const sectionContent = CartDiscount.resolveSectionTarget(section);
        if (!sectionHtml || !sectionContent) return;

        const innerHTML = CartDiscount.getSectionInnerHTML(sectionHtml, section.selector || '.shopify-section');
        if (innerHTML) sectionContent.innerHTML = innerHTML;
      });
    } catch (error) {
      console.error(error);
    }
  }

  formatErrors(errors) {
    if (!errors) return '';
    if (typeof errors === 'string') return errors;
    return Object.values(errors).flat().join(' ');
  }

  static async clearUnapprovedDiscounts() {
    if (CartDiscount.clearStarted) return;
    CartDiscount.clearStarted = true;

    if (!window.routes?.cart_url || !window.routes?.cart_update_url) {
      CartDiscount.clearStarted = false;
      return;
    }

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const appliedCodes = (cart.discount_codes || [])
        .filter((discount) => discount.applicable !== false)
        .map((discount) => discount.code)
        .filter(Boolean);

      const manualCodes = CartDiscount.getManualCodes();
      const approvedCodes = appliedCodes.filter((code) =>
        manualCodes.some((manual) => CartDiscount.normalizeCode(manual) === CartDiscount.normalizeCode(code))
      );

      const needsClear =
        appliedCodes.length > 0 &&
        (approvedCodes.length !== appliedCodes.length ||
          approvedCodes.some(
            (code, index) => CartDiscount.normalizeCode(code) !== CartDiscount.normalizeCode(appliedCodes[index])
          ));

      // Also clear when cart shows discount savings but no approved manual codes exist.
      const hasUnapprovedSavings =
        Number(cart.total_discount) > 0 &&
        approvedCodes.length === 0 &&
        (appliedCodes.length > 0 || (cart.cart_level_discount_applications || []).length > 0);

      if (!needsClear && !hasUnapprovedSavings) return;

      const sectionsToRender = CartDiscount.getGlobalSectionsToRender();
      const body = JSON.stringify({
        discount: approvedCodes.join(','),
        sections: sectionsToRender.map((section) => section.section),
        sections_url: `${window.location.pathname}${window.location.search}`,
      });

      const updateResponse = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
      const cartData = await updateResponse.json();

      if (!updateResponse.ok) return;

      const rendered = CartDiscount.renderSectionsFromCart(cartData);
      if (!rendered) {
        await CartDiscount.refreshSectionsFallback();
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount-clear', cartData });
      }
    } catch (error) {
      console.error(error);
    }
  }

  async onApply(presetCode) {
    const rawCode = typeof presetCode === 'string' ? presetCode : this.input.value;
    const code = CartDiscount.normalizeCode(rawCode);
    this.clearMessages();

    if (!code) {
      this.showError(window.cartDiscountStrings.emptyError);
      return;
    }

    // Keep input in sync without leading #
    if (this.input) this.input.value = code;

    this.setLoading(true);

    const previousCodes = CartDiscount.getManualCodes()
      .map((existing) => CartDiscount.normalizeCode(existing))
      .filter(Boolean);
    const sectionsToRender = this.getSectionsToRender();
    const sectionIds = sectionsToRender.map((section) => section.section);
    const sectionsUrl = `${window.location.pathname}${window.location.search}`;

    try {
      // Replace any previously applied code — only send the new one
      const body = JSON.stringify({
        discount: code,
        sections: sectionIds,
        sections_url: sectionsUrl,
      });

      const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
      const cartData = await response.json();

      if (!response.ok) {
        throw new Error(cartData.description || cartData.message || 'Discount request failed');
      }

      if (cartData.errors) {
        await this.restorePreviousCodes(previousCodes, code, sectionIds, sectionsUrl);
        this.showError(this.formatErrors(cartData.errors));
        return;
      }

      if (!this.isDiscountApplied(cartData, code)) {
        await this.restorePreviousCodes(previousCodes, code, sectionIds, sectionsUrl);
        const liveDiscount =
          document.querySelector('cart-drawer cart-discount') || document.querySelector('cart-discount') || this;
        const liveError = liveDiscount.querySelector?.('.cart__discount-error') || this.errorElement;
        if (liveError) {
          liveError.textContent = window.cartDiscountStrings.invalidError.replace('[code]', code);
          liveError.hidden = false;
        }
        return;
      }

      // Keep only the newly applied code in session
      CartDiscount.setManualCodes([code]);

      const rendered = this.renderSections(cartData);
      if (!rendered) {
        await this.refreshSectionsFallback();
      }

      // #CartDrawer refresh replaces this element — show success on the live instance
      const liveDiscount =
        document.querySelector('cart-drawer cart-discount') || document.querySelector('cart-discount');
      if (liveDiscount) {
        const liveInput = liveDiscount.querySelector('.cart__discount-input');
        const liveError = liveDiscount.querySelector('.cart__discount-error');
        const liveSuccess = liveDiscount.querySelector('.cart__discount-success');
        if (liveInput) liveInput.value = '';
        if (liveError) {
          liveError.hidden = true;
          liveError.textContent = '';
        }
        if (liveSuccess) {
          liveSuccess.textContent = window.cartDiscountStrings.success;
          liveSuccess.hidden = false;
        }
      }

      // Discount already applied — never surface subscriber errors in the discount UI
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData });
      } catch (publishError) {
        console.error(publishError);
      }
    } catch (error) {
      console.error(error);
      await this.restorePreviousCodes(previousCodes, code, sectionIds, sectionsUrl);
      const liveDiscount =
        document.querySelector('cart-drawer cart-discount') || document.querySelector('cart-discount') || this;
      const liveError = liveDiscount.querySelector?.('.cart__discount-error') || this.errorElement;
      if (liveError) {
        const friendly =
          error?.message && !/null|undefined|classList|innerHTML/i.test(error.message)
            ? error.message
            : window.cartStrings?.error || 'Discount request failed';
        liveError.textContent = friendly;
        liveError.hidden = false;
      }
    } finally {
      const liveDiscount =
        document.querySelector('cart-drawer cart-discount') || document.querySelector('cart-discount');
      if (liveDiscount && typeof liveDiscount.setLoading === 'function') {
        liveDiscount.setLoading(false);
      } else if (typeof this.setLoading === 'function') {
        this.setLoading(false);
      }
    }
  }

  async restorePreviousCodes(previousCodes, attemptedCode, sectionIds, sectionsUrl) {
    const codesToRestore = (previousCodes || []).filter(
      (existing) => CartDiscount.normalizeCode(existing) !== CartDiscount.normalizeCode(attemptedCode)
    );
    if (!codesToRestore.length) return;

    try {
      const restoreResponse = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          discount: codesToRestore.join(','),
          sections: sectionIds,
          sections_url: sectionsUrl,
        }),
      });
      const restoreData = await restoreResponse.json();
      if (!restoreResponse.ok) return;

      CartDiscount.setManualCodes(codesToRestore);
      const rendered = this.renderSections(restoreData);
      if (!rendered) {
        await this.refreshSectionsFallback();
      }
    } catch (error) {
      console.error(error);
    }
  }
}

customElements.define('cart-discount', CartDiscount);

document.addEventListener('DOMContentLoaded', () => {
  CartDiscount.clearUnapprovedDiscounts();
});
